import { lazy, type ComponentType } from 'react';

/**
 * Native module registry — maps module IDs to their lazy-loaded root components.
 *
 * To add a native module:
 * 1. Create a folder under src/modules/{module-id}/
 * 2. Export a default React component from src/modules/{module-id}/index.tsx
 * 3. Add a lazy import entry here
 * 4. Add the module to src/config/module-registry.config.ts with embeddingMethod: 'native'
 */
const nativeModules: Record<string, () => Promise<{ default: ComponentType }>> = {
  'conference-check-in-scanner': () => import('./conference-check-in-scanner'),
};

/**
 * Returns a lazy-loaded component for the given module ID, or null if not registered.
 * Components are cached so that React.lazy is only called once per module ID,
 * preventing "cannot create components during render" errors.
 */
const nativeComponentCache = new Map<string, ComponentType>();

export function getNativeModule(moduleId: string): ComponentType | null {
  const cached = nativeComponentCache.get(moduleId);
  if (cached) return cached;

  const loader = nativeModules[moduleId];
  if (!loader) return null;

  const LazyComponent = lazy(loader);
  nativeComponentCache.set(moduleId, LazyComponent);
  return LazyComponent;
}
